import React, { useState } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import { Tab, Tabs, Paper, Badge, Box } from "@material-ui/core";
import { makeStyles } from '@material-ui/core/styles';
import { processL2Approval, processStandardApproval, processRequestClarification, resetL2ApprovalState } from "../../redux/fui/index";
import Approvals from "./Approvals";
import DedicatedVpcApprovals from "./DedicatedVpcApprovals";

/**
 * Wrapper component for Approvals with tabs
 * @author shilpa.adikeshava
 * @version 1.0
 */

const useStyles = makeStyles((theme) => ({
  tabsContainer: {
    backgroundColor: '#ffffff',
    marginBottom: '24px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    border: '1px solid #e9ecef',
    overflow: 'hidden',
  },
  tabs: {
    backgroundColor: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
    '& .MuiTab-root': {
      minWidth: 300,
      textTransform: 'none',
      fontSize: '1rem',
      fontWeight: 600,
      color: '#495057',
      padding: '16px 32px',
      margin: '4px 8px',
      borderRadius: '8px',
      transition: 'all 0.3s ease',
      background: 'transparent',
      border: '2px solid #dee2e6',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(117, 0, 192, 0.04)',
        color: '#7500C0',
        borderColor: '#7500C0',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(117, 0, 192, 0.15)',
      },
      '&.Mui-selected': {
        color: '#ffffff',
        backgroundColor: '#7500C0',
        borderColor: '#7500C0',
        boxShadow: '0 4px 12px rgba(117, 0, 192, 0.3)',
        fontWeight: 700,
        '&:hover': {
          backgroundColor: '#5a0090',
          borderColor: '#5a0090',
          color: '#ffffff',
        },
      },
    },
    '& .MuiTabs-indicator': {
      display: 'none', // Hide default indicator since we're using background
    },
    '& .MuiTabs-flexContainer': {
      padding: '8px 16px',
      gap: '8px',
    },
  },
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: '#dc3545',
      color: '#ffffff',
      fontSize: '0.75rem',
      fontWeight: 600,
      minWidth: '20px',
      height: '20px',
      borderRadius: '10px',
      border: '2px solid #ffffff',
      marginLeft: '8px',
    },
  }
}));

const ApprovalsTabWrapper = ({
  email,
  processStandardApproval,
  processL2Approval,
  processRequestClarification,
  resetL2ApprovalState,
  isL2ApprovalInProgress,
  l2ApprovalResult,
  history
}) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [appPlatformCount, setAppPlatformCount] = useState(0);
  const [vpcCount, setVpcCount] = useState(0);
  const [vpcData, setVpcData] = useState([]);


  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get badge style based on tab selection state
  const getBadgeStyle = (isSelected) => ({
    backgroundColor: isSelected ? '#ffffff' : '#7500C0',
    color: isSelected ? '#7500C0' : '#ffffff',
    fontSize: '0.75rem',
    fontWeight: 600,
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px solid ${isSelected ? '#7500C0' : '#ffffff'}`,
    marginLeft: '12px',
  });

  return (
    <>
      {/* Always render VPC component for count but hidden when not active */}
      <div style={{ display: 'none' }}>
        <DedicatedVpcApprovals
          email={email}
          history={history}
          onCountChange={setVpcCount}
          onDataChange={setVpcData}
          countOnly={true}
        />
      </div>

      <Approvals
        email={email}
        processStandardApproval={processStandardApproval}
        processL2Approval={processL2Approval}
        processRequestClarification={processRequestClarification}
        resetL2ApprovalState={resetL2ApprovalState}
        isL2ApprovalInProgress={isL2ApprovalInProgress}
        l2ApprovalResult={l2ApprovalResult}
        history={history}
        activeTab={activeTab}
        handleTabChange={handleTabChange}
        onAppPlatformCountChange={setAppPlatformCount}
        tabsComponent={
          <Paper className={classes.tabsContainer}>
            <Tabs
              className={classes.tabs}
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={2} whiteSpace="nowrap">
                    <span>App / Platform Orders</span>
                    {appPlatformCount >= 0 && (
                      <span style={getBadgeStyle(activeTab === 0)}>
                        {appPlatformCount}
                      </span>
                    )}
                  </Box>
                }
              />
              <Tab
                label={
                  <Box display="flex" alignItems="center" gap={2} whiteSpace="nowrap">
                    <span>Dedicated VPC Orders</span>
                    {vpcCount >= 0 && (
                      <span style={getBadgeStyle(activeTab === 1)}>
                        {vpcCount}
                      </span>
                    )}
                  </Box>
                }
              />
            </Tabs>
          </Paper>
        }
        dedicatedVpcComponent={
          <DedicatedVpcApprovals
            email={email}
            history={history}
            onCountChange={setVpcCount}
            initialData={vpcData}
            skipInitialFetch={vpcData.length > 0}
          />
        }
      />
    </>
  );
};

ApprovalsTabWrapper.propTypes = {
  email: PropTypes.string,
  history: PropTypes.object,
  processStandardApproval: PropTypes.func,
  processL2Approval: PropTypes.func,
  processRequestClarification: PropTypes.func,
  resetL2ApprovalState: PropTypes.func,
  isL2ApprovalInProgress: PropTypes.bool,
  l2ApprovalResult: PropTypes.object,
};

const MSTP = (state) => ({
  email: state.auth.userDetails.email,
  isL2ApprovalInProgress: state.fui.isL2ApprovalInProgress,
  l2ApprovalResult: state.fui.l2ApprovalResult,
});

export default connect(MSTP, {
  processStandardApproval,
  processL2Approval,
  processRequestClarification,
  resetL2ApprovalState
})(withRouter(ApprovalsTabWrapper));